import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { WATER_HEATER } from "@volca/ui/components/ui/empty-machine";
import { InfoWithLabel } from "@volca/ui/components/ui/info-with-label";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { QuickbooksConnectionDetails } from "@/business/quickbooks";
import {
  isQuickbooksSyncing,
  quickbooksBillPushSummary,
  quickbooksConnectedLine,
} from "@/business/quickbooks-utils";
import { AP_PALETTE } from "@/core/colors";

/** A working connection: which company, who connected it, and what bills do. */
export const QuickbooksConnectedDetails = ({
  connection,
  canManage,
  onDisconnect,
}: {
  connection: QuickbooksConnectionDetails;
  canManage: boolean;
  onDisconnect: () => void;
}) => (
  <Stack gap={6} align="start" className="w-full">
    <Stack align="start" gap={1}>
      <Stack direction="row" gap={2} align="center">
        <Text variant="label">
          {connection.company_name ?? "QuickBooks company"}
        </Text>
        {/* Production is the normal case, so only sandbox gets a badge. */}
        {connection.environment === "sandbox" && (
          <Badge variant="secondary" color={AP_PALETTE.neutral.tone} size="sm">
            Sandbox
          </Badge>
        )}
      </Stack>
      <Text tone="muted">{quickbooksConnectedLine(connection)}</Text>
    </Stack>

    {isQuickbooksSyncing(connection) && (
      <MachineEmptyState
        art={WATER_HEATER}
        active
        title="Preparing QuickBooks"
        description="Your accounts are syncing for the first time."
        footnote="Bill settings will be available when this sync finishes."
      />
    )}

    {connection.bill_push_available && (
      <InfoWithLabel label="Bills">
        {quickbooksBillPushSummary(connection)}
      </InfoWithLabel>
    )}

    {canManage && (
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit"
        onClick={onDisconnect}
      >
        Disconnect
      </Button>
    )}
  </Stack>
);
